/**
 * AbstractMeteredConnection - shared base class for INetworkConnection
 * implementations that adds bandwidth + RTT bookkeeping.
 *
 * Subclasses implement the actual transport (WebSocket, WebTransport, ...)
 * and call `trackSent()` / `trackReceived()` whenever bytes pass through.
 * Implementations with access to wire-accurate counters (e.g. Node `ws`
 * exposes `_socket.bytesRead`/`bytesWritten`) may also override `getStats()`
 * to surface those numbers under `extra`.
 */
import { Delegate } from '../../utils/Delegate.js';

import type { ConnectionStatsSnapshot, INetworkConnection } from '../interface.js';

export abstract class AbstractMeteredConnection implements INetworkConnection {
  public readonly onOpen: Delegate<[], void> = new Delegate();
  public readonly onMessage: Delegate<[Uint8Array], void> = new Delegate();
  public readonly onClose: Delegate<[number, string], void> = new Delegate();
  public readonly onError: Delegate<[Error], void> = new Delegate();

  private bytesSentTotal: number = 0;
  private bytesReceivedTotal: number = 0;
  private messagesSentTotal: number = 0;
  private messagesReceivedTotal: number = 0;

  private bytesSentInWindow: number = 0;
  private bytesReceivedInWindow: number = 0;
  private windowStartedAt: number = Date.now();
  private lastBytesSentPerSecond: number = 0;
  private lastBytesReceivedPerSecond: number = 0;

  private rttMs: number | null = null;
  private connectedAt: number = 0;

  protected constructor() {
    this.onOpen.add(() => {
      if (this.connectedAt === 0) {
        this.connectedAt = Date.now();
      }
    });
  }

  public abstract isOpen(): boolean;
  public abstract connect(): void;
  public abstract send(data: Uint8Array): void;
  public abstract close(code?: number, reason?: string): void;

  /**
   * Subclasses call this with the byte length of every outbound message.
   */
  protected trackSent(byteCount: number): void {
    this.bytesSentTotal += byteCount;
    this.bytesSentInWindow += byteCount;
    this.messagesSentTotal += 1;
  }

  /**
   * Subclasses call this with the byte length of every inbound message.
   */
  protected trackReceived(byteCount: number): void {
    this.bytesReceivedTotal += byteCount;
    this.bytesReceivedInWindow += byteCount;
    this.messagesReceivedTotal += 1;
  }

  /**
   * Force the rolling 1s window to be refreshed if more than ~1s has
   * elapsed since the last refresh. Called lazily from `getStats()`.
   */
  private refreshWindowIfDue(): void {
    const now = Date.now();
    const elapsedMs = now - this.windowStartedAt;
    if (elapsedMs >= 1000) {
      this.lastBytesSentPerSecond = Math.round((this.bytesSentInWindow * 1000) / elapsedMs);
      this.lastBytesReceivedPerSecond = Math.round((this.bytesReceivedInWindow * 1000) / elapsedMs);
      this.bytesSentInWindow = 0;
      this.bytesReceivedInWindow = 0;
      this.windowStartedAt = now;
    }
  }

  /**
   * Optional hook for subclasses to attach transport-specific extras
   * (bufferedAmount, TCP byte counters, etc.) to the snapshot.
   */
  protected collectExtra(): Record<string, number> | undefined {
    return undefined;
  }

  public getStats(): ConnectionStatsSnapshot {
    this.refreshWindowIfDue();
    return {
      bytesSent: this.bytesSentTotal,
      bytesReceived: this.bytesReceivedTotal,
      messagesSent: this.messagesSentTotal,
      messagesReceived: this.messagesReceivedTotal,
      bytesSentPerSecond: this.lastBytesSentPerSecond,
      bytesReceivedPerSecond: this.lastBytesReceivedPerSecond,
      rttMs: this.rttMs,
      connectedAt: this.connectedAt,
      extra: this.collectExtra()
    };
  }

  public recordRttSample(rttMs: number): void {
    if (Number.isFinite(rttMs) && rttMs >= 0) {
      this.rttMs = rttMs;
    }
  }

  /**
   * Subclasses that accept already-open connections (e.g. server-accepted
   * sockets) can call this to set the `connectedAt` timestamp without
   * waiting for an `onOpen` event that will never fire.
   */
  protected markConnected(): void {
    if (this.connectedAt === 0) {
      this.connectedAt = Date.now();
    }
  }
}
